import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import TrackInfo from './pages/TrackInfo';
import Services from './pages/client/Services';
import Events from './pages/client/Events';
import Gallery from './pages/Gallery';
import Profile from './pages/client/Profile';
import DriverProfile from './pages/client/DriverProfile';
import Register from './pages/Register';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangeTemporaryPassword from './pages/ChangeTemporaryPassword';
import UpdateEmail from './pages/client/UpdateEmail';
import UpdatePassword from './pages/client/UpdatePassword';
import TournamentResults from './pages/client/TournamentResults';
import AdminDashboard from './pages/admin/AdminDashboard';
import WorkerDashboard from './pages/worker/Dashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import RoleManagement from './pages/admin/RoleManagement';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Disclaimer from './pages/Disclaimer';
import ErrorBoundary from './components/ErrorBoundary';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, workerOnly = false, clientOnly = false }: { 
  children: React.ReactNode, 
  adminOnly?: boolean,
  workerOnly?: boolean,
  clientOnly?: boolean
}) => {
  const { user, isAdmin, isWorker } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to={isWorker ? "/worker" : "/profile"} replace />;
  }

  if (workerOnly && !isWorker) {
    return <Navigate to={isAdmin ? "/admin" : "/profile"} replace />;
  }

  if (clientOnly && (isAdmin || isWorker)) {
    return <Navigate to={isAdmin ? "/admin" : "/worker"} replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background-primary text-text-primary">
        <Navbar />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/track-info" element={<TrackInfo />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-temporary-password" element={<ChangeTemporaryPassword />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/disclaimer" element={<Disclaimer />} />

          {/* Client Routes */}
          <Route 
            path="/services" 
            element={
              <ProtectedRoute>
                <Services />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/events" 
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute clientOnly>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/driver-profile" 
            element={
              <ProtectedRoute clientOnly>
                <DriverProfile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/update-email" 
            element={
              <ProtectedRoute>
                <UpdateEmail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/update-password" 
            element={
              <ProtectedRoute>
                <UpdatePassword />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournament-results" 
            element={<TournamentResults />} 
          />

          {/* Worker Routes */}
          <Route 
            path="/worker/*" 
            element={
              <ProtectedRoute workerOnly>
                <WorkerDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Admin Routes */}
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Role Management Route */}
          <Route 
            path="/admin/roles" 
            element={
              <ProtectedRoute adminOnly>
                <RoleManagement />
              </ProtectedRoute>
            } 
          />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Footer />
        <Toaster position="top-center" />
        <SpeedInsights />
      </div>
    </ErrorBoundary>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <PermissionProvider>
          <App />
        </PermissionProvider>
      </AuthProvider>
    </Router>
  );
}